import { getBaseUrlAndSubdomain } from '@util/getURL';
import { RedirectType, redirect } from 'next/navigation';
import { client } from './contentfulClient';
import { fixLocale } from './page';
import { apiClient } from 'api-client';
import evAPI from '@util/api/ev';

export interface Path {
  tags: any;
  current: string;
  title: string;
  id: string;
}

// promise style
export const getArticles = async (tags?: any): Promise<Path[]> => {
  // convert tags from array to comma separated string
  const tagsInCommas = tags?.join(',');
  const results = await client().getEntries({
    content_type: 'article',
    'metadata.tags.sys.id[in]': tagsInCommas,
    limit: 1000,
  });
  return results.items.map((page) => {
    // TODO: map Contentful types into TS types
    // @ts-ignore
    return {
      current: String(page.fields.slug) || '',
      title: String(page.fields.title) || '',
      id: String(page.sys.id) || '',
      tags: page.metadata.tags || [],
    };
  });
};

export const getBlogPostsByTag = async (
  tags: string,
  limit?: number,
  locale?: string,
  preview?: boolean,
): Promise<any> => {
  if (locale == null) locale = 'en-US';
  const params = {
    content_type: 'article',
    locale: fixLocale(locale),
    include: 10,
    limit: limit ? limit : 1000,
    order: ['-fields.publicationDate', '-sys.createdAt'],
  };
  if (tags?.length > 0) {
    params['metadata.tags.sys.id[in]'] = [tags];
  }
  const clientEntries = await client().getEntries(params);
  return clientEntries;
};

export const checkIfBlogHomepageExists = async (slug: string, preview?: boolean): Promise<any> => {
  const clientEntries = await client().getEntries({
    'fields.slug': slug,
    content_type: 'blogHomepage',
    include: 1,
    limit: 1,
  });
  if (clientEntries.items.length > 0) return true;
  return false;
};

// async await style
export const getBlogDetailPageData = async (
  locale: string,
  slug: string,
  preview?: boolean,
): Promise<any> => {
  if (locale == null || locale == 'en') locale = 'en-US';
  const blogEntry = await client().getEntries({
    'fields.slug[in]': slug,
    content_type: 'article',
    limit: 1,
    include: 8,
    locale: fixLocale(locale),
  });

  return blogEntry;
};

export const checkBlogSubdomain = async (host: string, tagIds: string[] = []) => {
  const api = new evAPI();
  const { subdomain } = await getBaseUrlAndSubdomain(host);

  // If no contentful tag, then the article does not exist
  if (tagIds.length === 0) {
    redirect('/404');
  }

  if (
    (subdomain === 'americas' || subdomain === null) &&
    (tagIds.includes('americas') || tagIds.includes('americasOptional'))
  ) {
    return;
  }
  const shops = await api.searchShops({ shops: tagIds.join(',') });
  const subDomains = shops?.records.map((shop) =>
    shop.contact?.website.split('.')[0].toLowerCase(),
  );
  if (!subDomains.includes(subdomain)) {
    redirect('/404');
  }
};

export const getBlogPageSimilarPosts = async (
  shopTags: any, // will be an array of tags
  blogTags: any, // will be an array of tags
  currentSlug: string,
  preview?: boolean,
): Promise<any> => {
  //change array to comma separated string
  let shopTagsInCommas = '';
  if (shopTags && shopTags.length > 0) {
    shopTagsInCommas = shopTags.map((tag) => tag.sys.id).join(',');
  }
  let blogTagsInCommas = '';
  if (blogTags && blogTags.length > 0) {
    blogTagsInCommas = blogTags.join(',');
  }

  const blogEntry = await client().getEntries({
    'metadata.tags.sys.id[in]': [shopTagsInCommas],
    'fields.blogTags[in]': [blogTagsInCommas],
    'fields.slug[nin]': currentSlug,
    content_type: 'article',
    order: ['-fields.publicationDate', '-sys.createdAt'],
    limit: 3,
  });
  return blogEntry;
};
